import { Command } from 'commander';
import chalk from 'chalk';
import inquirer from 'inquirer';
import ora from 'ora';
import { TaskMigration } from '../core/task-migration';
import { logger } from '../utils/logger';

export function createMigrateCommand(): Command {
  const command = new Command('migrate');
  
  command
    .description('迁移任务数据到新的扩展格式')
    .option('--check', '只检查是否需要迁移，不执行实际迁移')
    .option('--force', '强制迁移，跳过确认')
    .option('--validate', '迁移后验证数据完整性')
    .action(async (options) => {
      try {
        const migration = new TaskMigration();
        
        // 检查迁移状态
        const spinner = ora('检查迁移状态...').start();
        const status = await migration.getStatus();
        spinner.stop();
        
        console.log(chalk.bold('\n迁移状态:'));
        console.log(`当前版本: ${status.currentVersion}`);
        console.log(`目标版本: ${status.targetVersion}`);
        console.log(`任务数量: ${status.taskCount}`);
        console.log(`需要迁移: ${status.needsMigration ? chalk.yellow('是') : chalk.green('否')}`);
        
        if (options.check) {
          return;
        }
        
        if (!status.needsMigration && status.currentVersion === status.targetVersion) {
          console.log(chalk.green('\n✅ 任务数据已经是最新格式，无需迁移'));
          return;
        }
        
        // 确认迁移
        if (!options.force) {
          console.log(chalk.yellow('\n⚠️  迁移说明:'));
          console.log('1. 迁移将扩展任务数据结构，添加以下字段:');
          console.log('   - specId, designId, workflowId: 文档关联');
          console.log('   - agentAssignments: AI Agent 分配信息');
          console.log('   - promptTemplates: Prompt 模板配置');
          console.log('   - artifacts: 任务产出物追踪');
          console.log('2. 迁移前会自动创建备份');
          console.log('3. 迁移过程是幂等的，可以安全地多次执行');
          
          const { confirm } = await inquirer.prompt({
            type: 'confirm',
            name: 'confirm',
            message: '是否继续迁移？',
            default: true
          });
          
          if (!confirm) {
            console.log(chalk.yellow('已取消迁移'));
            return;
          }
        }
        
        // 执行迁移
        const migrateSpinner = ora('执行任务迁移...').start();
        const result = await migration.migrate();
        
        if (result.success) {
          migrateSpinner.succeed(`成功迁移 ${result.migratedTasks}/${result.totalTasks} 个任务`);
          
          if (result.backupPath) {
            console.log(chalk.gray(`备份位置: ${result.backupPath}`));
          }
          
          if (result.errors.length > 0) {
            console.log(chalk.yellow('\n⚠️  迁移警告:'));
            result.errors.forEach(error => {
              console.log(chalk.yellow(`- ${error}`));
            });
          }
        } else {
          migrateSpinner.fail('迁移失败');
          console.log(chalk.red('\n错误信息:'));
          result.errors.forEach(error => {
            console.log(chalk.red(`- ${error}`));
          });
          return;
        }
        
        // 验证迁移结果
        if (options.validate) {
          const validateSpinner = ora('验证迁移结果...').start();
          const validation = await migration.validate();
          
          if (validation.valid) {
            validateSpinner.succeed('数据验证通过');
          } else {
            validateSpinner.fail('数据验证失败');
            console.log(chalk.red('\n验证问题:'));
            validation.issues.forEach(issue => {
              console.log(chalk.red(`- ${issue}`));
            });
          }
        }
        
        // 显示迁移后的示例
        console.log(chalk.bold('\n迁移完成！新的任务结构示例:'));
        console.log(chalk.gray(`
{
  "id": "task-123",
  "content": "实现用户登录功能",
  "status": "in_progress",
  "priority": "high",
  
  // 新增的关联字段
  "specId": "REQ-AUTH-001",
  "designId": "DESIGN-AUTH-001",
  "workflowId": "workflow-auth",
  "prdId": "PRD-2024-001",
  
  // AI 协作信息
  "agentAssignments": {
    "primary": "agent-frontend",
    "reviewers": ["agent-reviewer"],
    "assignedAt": "2024-01-01T10:00:00Z"
  },
  
  // 任务产出
  "artifacts": {
    "code": {
      "files": ["src/login.tsx", "src/auth.ts"],
      "commits": ["abc123"],
      "stats": { "added": 150, "modified": 20 }
    }
  }
}`));
        
        console.log(chalk.green('\n✅ 任务数据迁移成功！'));
        console.log(chalk.gray('提示: 使用 "workflow task list" 查看迁移后的任务'));
        
      } catch (error: any) {
        logger.error('迁移命令执行失败:', error.message);
        console.log(chalk.red(`\n错误: ${error.message}`));
      }
    });
  
  return command;
}